import type { BlockTypeError, ConnectionError } from '@/queries/pipelines';
import type { YamlEditorDiagnostic } from 'ui-components';
import { parse } from 'yaml';

/**
 * Converts validation errors to YAML editor diagnostics for inline error display
 */
export const convertValidationErrorsToDiagnostics = (
  yamlContent: string,
  errors: (BlockTypeError | ConnectionError)[],
  warnings: ConnectionError[] = [],
): YamlEditorDiagnostic[] => {
  const diagnostics: YamlEditorDiagnostic[] = [];

  try {
    // Parse YAML to get position information
    const yamlDocument = parse(yamlContent, { keepSourceTokens: true });

    // Process errors
    for (const error of errors) {
      const diagnostic = createDiagnosticFromError(
        yamlContent,
        error,
        yamlDocument,
      );
      if (diagnostic) {
        diagnostics.push(diagnostic);
      }
    }

    // Process warnings
    for (const warning of warnings) {
      const diagnostic = createDiagnosticFromError(
        yamlContent,
        warning,
        yamlDocument,
        'warning',
      );
      if (diagnostic) {
        diagnostics.push(diagnostic);
      }
    }
  } catch (parseError) {
    // If we can't parse the YAML, create a generic diagnostic for the parsing error
    const diagnostic = createParseErrorDiagnostic(yamlContent, parseError);
    if (diagnostic) {
      diagnostics.push(diagnostic);
    }
  }

  return diagnostics;
};

/**
 * Creates a diagnostic from a validation error
 */
const createDiagnosticFromError = (
  yamlContent: string,
  error: BlockTypeError | ConnectionError,
  yamlDocument: unknown,
  severity: 'error' | 'warning' = 'error',
): YamlEditorDiagnostic | null => {
  if (error.type === 'block_type_error') {
    return createBlockErrorDiagnostic(
      yamlContent,
      error,
      yamlDocument,
      severity,
    );
  } else if (error.type === 'connection_error') {
    return createConnectionErrorDiagnostic(
      yamlContent,
      error,
      yamlDocument,
      severity,
    );
  }
  return null;
};

/**
 * Creates a diagnostic for block-related errors
 */
const createBlockErrorDiagnostic = (
  yamlContent: string,
  error: BlockTypeError,
  _yamlDocument: unknown,
  severity: 'error' | 'warning',
): YamlEditorDiagnostic | null => {
  try {
    // Try to find the block in the YAML structure
    const blockPosition = findBlockPosition(yamlContent, error.blockId);
    if (blockPosition) {
      return {
        from: blockPosition.from,
        to: blockPosition.to,
        severity,
        message: error.message,
        source: `Block: ${error.blockId}`,
      };
    }

    // Fallback: highlight the entire blocks section if we can't find the specific block
    const blocksPosition = findSectionPosition(yamlContent, 'blocks');
    if (blocksPosition) {
      return {
        from: blocksPosition.from,
        to: blocksPosition.to,
        severity,
        message: error.message,
        source: `Block: ${error.blockId}`,
      };
    }
  } catch (e) {
    // If position finding fails, fall back to highlighting the entire document
  }

  return createFallbackDiagnostic(yamlContent, error.message, severity);
};

/**
 * Creates a diagnostic for connection-related errors
 */
const createConnectionErrorDiagnostic = (
  yamlContent: string,
  error: ConnectionError,
  _yamlDocument: unknown,
  severity: 'error' | 'warning',
): YamlEditorDiagnostic | null => {
  try {
    // Try to find the specific connection in the YAML structure
    const connectionPosition = findConnectionPosition(
      yamlContent,
      error.sourceBlockId,
      error.targetBlockId,
    );
    if (connectionPosition) {
      return {
        from: connectionPosition.from,
        to: connectionPosition.to,
        severity,
        message: error.message,
        source: `Connection: ${error.connectionId}`,
      };
    }

    // Fallback: highlight the entire connections section
    const connectionsPosition = findSectionPosition(yamlContent, 'connections');
    if (connectionsPosition) {
      return {
        from: connectionsPosition.from,
        to: connectionsPosition.to,
        severity,
        message: error.message,
        source: `Connection: ${error.connectionId}`,
      };
    }
  } catch (e) {
    // If position finding fails, fall back to highlighting the entire document
  }

  return createFallbackDiagnostic(yamlContent, error.message, severity);
};

/**
 * Creates a diagnostic for YAML parsing errors
 */
const createParseErrorDiagnostic = (
  yamlContent: string,
  parseError: unknown,
): YamlEditorDiagnostic | null => {
  // Try to extract line information from the parse error
  if (
    parseError &&
    typeof parseError === 'object' &&
    'linePos' in parseError &&
    Array.isArray(parseError.linePos)
  ) {
    const lineStart = getLineStartPosition(
      yamlContent,
      parseError.linePos[0].line - 1,
    );
    const lineEnd = getLineEndPosition(
      yamlContent,
      parseError.linePos[0].line - 1,
    );

    return {
      from: lineStart,
      to: lineEnd,
      severity: 'error',
      message: `YAML Parse Error: ${
        (parseError as { message?: string }).message || 'Invalid YAML syntax'
      }`,
      source: 'YAML Parser',
    };
  }

  return createFallbackDiagnostic(
    yamlContent,
    'YAML Parse Error: Invalid syntax',
    'error',
  );
};

/**
 * Finds the position of a specific block in the YAML content
 */
const findBlockPosition = (
  yamlContent: string,
  blockId: string,
): { from: number; to: number } | null => {
  const lines = yamlContent.split('\n');
  let currentPos = 0;
  let inBlocksSection = false;
  let blockStartPos = -1;
  let blockEndPos = -1;
  let currentIndent = 0;

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    const trimmedLine = line.trim();

    // Check if we're entering the blocks section
    if (trimmedLine === 'blocks:' || trimmedLine.startsWith('blocks:')) {
      inBlocksSection = true;
      currentPos += line.length + 1; // +1 for newline
      continue;
    }

    if (inBlocksSection) {
      // Check if we're exiting the blocks section (next top-level key)
      if (
        trimmedLine &&
        !line.startsWith(' ') &&
        !line.startsWith('\t') &&
        trimmedLine.includes(':')
      ) {
        break;
      }

      // Look for the specific block ID
      if (
        trimmedLine === `${blockId}:` ||
        trimmedLine.startsWith(`${blockId}:`)
      ) {
        blockStartPos = currentPos;
        currentIndent = line.length - line.trimStart().length;
        currentPos += line.length + 1;
        continue;
      }

      // If we found the block start, find the end
      if (blockStartPos !== -1) {
        const lineIndent = line.length - line.trimStart().length;

        // If we hit a line with same or less indentation (and it's not empty), we've found the end
        if (trimmedLine && lineIndent <= currentIndent) {
          blockEndPos = currentPos;
          break;
        }
      }
    }

    currentPos += line.length + 1; // +1 for newline
  }

  // If we found a block but didn't find an end, use the current position
  if (blockStartPos !== -1 && blockEndPos === -1) {
    blockEndPos = currentPos;
  }

  return blockStartPos !== -1 ? { from: blockStartPos, to: blockEndPos } : null;
};

/**
 * Finds the position of a specific connection in the YAML content
 */
const findConnectionPosition = (
  yamlContent: string,
  sourceBlockId: string,
  targetBlockId: string,
): { from: number; to: number } | null => {
  const lines = yamlContent.split('\n');
  let currentPos = 0;
  let inConnectionsSection = false;

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    const trimmedLine = line.trim();

    // Check if we're entering the connections section
    if (
      trimmedLine === 'connections:' ||
      trimmedLine.startsWith('connections:')
    ) {
      inConnectionsSection = true;
      currentPos += line.length + 1;
      continue;
    }

    if (inConnectionsSection) {
      // Check if we're exiting the connections section
      if (
        trimmedLine &&
        !line.startsWith(' ') &&
        !line.startsWith('\t') &&
        trimmedLine.includes(':')
      ) {
        break;
      }

      // Look for connection entries that match our source and target
      if (
        trimmedLine.includes(`source_block_id: ${sourceBlockId}`) ||
        trimmedLine.includes(`target_block_id: ${targetBlockId}`)
      ) {
        // Find the start of this connection object (usually starts with "- ")
        let connectionStart = currentPos;

        // Look backwards to find the start of the connection
        for (let j = i - 1; j >= 0; j--) {
          const prevLine = lines[j];
          const prevTrimmed = prevLine.trim();

          if (prevTrimmed.startsWith('- ')) {
            connectionStart = currentPos - (line.length + 1);
            for (let k = j + 1; k < i; k++) {
              connectionStart -= lines[k].length + 1;
            }
            break;
          }
        }

        // Find the end of this connection object
        let connectionEnd = currentPos + line.length;
        for (let j = i + 1; j < lines.length; j++) {
          const nextLine = lines[j];
          const nextTrimmed = nextLine.trim();

          if (
            nextTrimmed.startsWith('- ') ||
            (!nextLine.startsWith(' ') && !nextLine.startsWith('\t'))
          ) {
            break;
          }
          connectionEnd += nextLine.length + 1;
        }

        return { from: connectionStart, to: connectionEnd };
      }
    }

    currentPos += line.length + 1;
  }

  return null;
};

/**
 * Finds the position of a top-level section in the YAML content
 */
const findSectionPosition = (
  yamlContent: string,
  sectionName: string,
): { from: number; to: number } | null => {
  const lines = yamlContent.split('\n');
  let currentPos = 0;
  let sectionStart = -1;
  let sectionEnd = -1;

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    const trimmedLine = line.trim();

    // Look for the section
    if (
      trimmedLine === `${sectionName}:` ||
      trimmedLine.startsWith(`${sectionName}:`)
    ) {
      sectionStart = currentPos;
      currentPos += line.length + 1;
      continue;
    }

    // If we found the section start, find the end
    if (sectionStart !== -1) {
      // If we hit a line that's not indented (top-level key), we've found the end
      if (
        trimmedLine &&
        !line.startsWith(' ') &&
        !line.startsWith('\t') &&
        trimmedLine.includes(':')
      ) {
        sectionEnd = currentPos;
        break;
      }
    }

    currentPos += line.length + 1;
  }

  // If we found a section but didn't find an end, use the current position
  if (sectionStart !== -1 && sectionEnd === -1) {
    sectionEnd = currentPos;
  }

  return sectionStart !== -1 ? { from: sectionStart, to: sectionEnd } : null;
};

/**
 * Gets the start position of a specific line
 */
const getLineStartPosition = (
  yamlContent: string,
  lineIndex: number,
): number => {
  const lines = yamlContent.split('\n');
  let position = 0;

  for (let i = 0; i < lineIndex && i < lines.length; i++) {
    position += lines[i].length + 1; // +1 for newline
  }

  return position;
};

/**
 * Gets the end position of a specific line
 */
const getLineEndPosition = (yamlContent: string, lineIndex: number): number => {
  const lines = yamlContent.split('\n');
  let position = 0;

  for (let i = 0; i <= lineIndex && i < lines.length; i++) {
    position += lines[i].length + (i < lines.length - 1 ? 1 : 0); // +1 for newline except last line
  }

  return position;
};

/**
 * Creates a fallback diagnostic that highlights the entire document
 */
const createFallbackDiagnostic = (
  yamlContent: string,
  message: string,
  severity: 'error' | 'warning',
): YamlEditorDiagnostic => {
  return {
    from: 0,
    to: Math.min(yamlContent.length, 100), // Highlight first 100 characters as fallback
    severity,
    message,
    source: 'Pipeline Validation',
  };
};
